import { SegmentHeader } from "@/components/campaign/basic/SegmentHeader";
import { ListItem } from "@/components/campaign/ListItem";
import { Select, SelectItem } from "@/components/generic/Select";
import { TagField } from "@/components/generic/TagField";
import { FilterGroupComponentProps } from "@/data/filters/filter-types";
import { regions } from "@/data/regions";
import { useReportDirty } from "@/hooks/use-report-dirty";
import { ComparisonOperator, comparisonOperators } from "@/utils/operators";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "react-aria-components";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

const schema = z.object({
  operator: comparisonOperators.enum,
  values: z.array(z.number()).min(1, {
    message: "Du skal vælge mindst en region",
  }),
});

export function RegionsSegment({
  queryFilters,
  update,
  formId,
  setIsDirty,
}: FilterGroupComponentProps<
  "value",
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  {}
>) {
  const regionsFilter = queryFilters.value;

  const { control, handleSubmit, watch, setValue, formState } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      operator: (regionsFilter?.operator as ComparisonOperator) || "Equal",
      values: regionsFilter?.values?.map(Number) || [],
    },
  });

  useReportDirty(formState.isDirty, setIsDirty);

  const { values, operator } = watch();

  const submit = handleSubmit((data) => {
    update({
      value: { ...data, values: data.values.map(String) },
    });
  });

  return (
    <div className="flex flex-col gap-y-9">
      <SegmentHeader
        title="Region"
        description="Vælg en eller flere regioner."
      />
      <Form
        onSubmit={submit}
        className="flex h-full flex-col items-center justify-between gap-y-12"
        id={formId}
      >
        <div className="flex flex-col gap-y-6">
          <div className="mx-auto flex gap-x-4">
            <Controller
              control={control}
              name="operator"
              render={({ field, fieldState: { error } }) => (
                <Select
                  placeholder="Operator"
                  aria-label="Operator"
                  selectedKey={field.value}
                  onSelectionChange={field.onChange}
                  items={Object.entries(comparisonOperators.values).map(
                    ([key, value]) => ({
                      id: key,
                      label: value,
                    }),
                  )}
                  errorMessage={error?.message}
                >
                  {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                </Select>
              )}
            />
            <Controller
              control={control}
              name="values"
              render={({ field, fieldState: { error } }) => (
                <TagField
                  placeholder="Region"
                  aria-label="Region"
                  selectedOptions={
                    field.value.map((value) => ({
                      label: regions.find((r) => r.value === Number(value))!
                        .title,
                      value: Number(value),
                    })) || []
                  }
                  setSelectedOptions={(value) => {
                    if (Array.isArray(value))
                      return field.onChange(value.map((item) => item.value));
                  }}
                  options={regions.map((region) => ({
                    label: region.title,
                    value: region.value,
                  }))}
                  errorMessage={error?.message}
                />
              )}
            />
          </div>
          <ul className="flex flex-col gap-y-2">
            {values.map((value, index) => (
              <ListItem
                key={value}
                label={`${operator === "Equal" ? "Lig med" : "Ikke lig med"} ${
                  regions.find((r) => r.value === Number(value))?.title
                }`}
                onRemove={() => {
                  const newValues = [...values];
                  newValues.splice(index, 1);
                  setValue("values", newValues);
                }}
              />
            ))}
          </ul>
        </div>
      </Form>
    </div>
  );
}
